/**
 * CancelFuRequestDialog — confirms a follow-up-request cancellation.
 *
 * Mirrors Flutter `fu_requests_tab.dart:215-356` (`showCancelAlert`).
 * Simpler than the appointment-cancel dialog — no recurring-scope
 * picker, just a reason dropdown + patient-aware confirmation copy.
 *
 * Wire-shape verified against the 2026-05-20 dev curl batch for
 * `POST /api/Appointment/CancelRequest`. Reason payload reuses
 * `AppointmentCancellationReason` — same value-set the appointment-
 * cancel flow uses (`useAppointmentCancellationReasons`).
 *
 * UX rules from Flutter:
 *   - Title: `"Do you really want to cancel this request?"`
 *   - Body: `"When you cancel we will notify ${patient.firstName}
 *     about this change using their preferred communication method."`
 *     (verbatim — Flutter `:226-244`)
 *   - Submit button disabled until reason picked (`:309-313` —
 *     `disabledBtnBGColor → enabledBtnBGColor` flip)
 *   - On 200: dialog closes; the FU list refetches via cache
 *     invalidation in `useCancelFollowUpRequest`
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAppointmentCancellationReasons } from '@/scheduling/services/use-appointment-cancellation-reasons';
import { useCancelFollowUpRequest } from '@/scheduling/services/use-cancel-follow-up-request';

export interface CancelFuRequestTarget {
  fuRequestId: string;
  /** First name used in the verbatim Flutter confirmation message.
   *  Falls back to "the patient" if absent. */
  patientFirstName?: string | null;
}

interface CancelFuRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: CancelFuRequestTarget | null;
}

export function CancelFuRequestDialog({
  open,
  onOpenChange,
  target,
}: CancelFuRequestDialogProps) {
  const { data: reasons = [], isLoading: reasonsLoading } =
    useAppointmentCancellationReasons();
  const cancelMutation = useCancelFollowUpRequest();

  const [reasonCode, setReasonCode] = useState('');

  // Reset selection when the dialog closes so the next open starts
  // fresh. Matches the cancel-appointment-dialog pattern.
  useEffect(() => {
    if (!open) {
      setReasonCode('');
    }
  }, [open]);

  const canSubmit =
    !!target && !!reasonCode && !reasonsLoading && !cancelMutation.isPending;

  const submit = async () => {
    if (!target || !reasonCode) return;
    const reasonObj = reasons.find((r) => r.code === reasonCode);
    if (!reasonObj) return;

    try {
      await cancelMutation.mutateAsync({
        request: {
          fuRequestId: target.fuRequestId,
          cancellationReason: reasonObj,
        },
      });
      onOpenChange(false);
    } catch {
      // Global QueryClient onError surfaces the failure as a toast.
      // Keep the dialog open so the user can retry without re-picking.
    }
  };

  const patientFirstName = target?.patientFirstName?.trim() || 'the patient';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[var(--dialog-width-confirm)]">
        <DialogHeader>
          <DialogTitle>Do you really want to cancel this request?</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <p className="[font:var(--type-body)] text-[var(--ink-body)]">
            When you cancel we will notify <strong>{patientFirstName}</strong>{' '}
            about this change using their preferred communication method.
          </p>

          <div>
            <label
              htmlFor="cancel-fu-reason"
              className="text-sm font-semibold text-text-primary block mb-2"
            >
              Cancellation reason
              <span className="ml-1 text-[var(--status-bad)]">*</span>
            </label>
            <select
              id="cancel-fu-reason"
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              disabled={reasonsLoading || cancelMutation.isPending}
              className="w-full h-10 px-3 text-sm rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:opacity-60"
            >
              <option value="">
                {reasonsLoading ? 'Loading reasons…' : 'Select a reason'}
              </option>
              {reasons.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.display}
                </option>
              ))}
            </select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={cancelMutation.isPending}
          >
            Keep request
          </Button>
          <Button
            variant="destructive"
            onClick={() => void submit()}
            disabled={!canSubmit}
          >
            {cancelMutation.isPending ? 'Cancelling…' : 'Cancel request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
